import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { 
  LogOut, BookOpen, Trophy, TrendingUp, Clock, Target, 
  Atom, FlaskConical, Calculator, Dna, ArrowRight, Sparkles,
  CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

const EXAM_CONFIG = {
  'JEE Mains': { color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-500/10' },
  'JEE Advanced': { color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-500/10' },
  'NEET': { color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-500/10' }
};

const SUBJECT_ICONS = {
  'Physics': Atom,
  'Chemistry': FlaskConical,
  'Mathematics': Calculator,
  'Biology': Dna
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [purchasesRes, attemptsRes] = await Promise.all([
        api.get('/user/purchases'),
        api.get('/user/attempts')
      ]);
      setPurchases(purchasesRes.data);
      setAttempts(attemptsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role === 'admin') {
      navigate('/admin');
      return;
    }
    fetchDashboardData();
  }, [user, navigate, fetchDashboardData]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getTestExamType = (test) => {
    if (test.exam_type) return test.exam_type;
    const mapping = { 'JEE Main': 'JEE Mains', 'JEE Advanced': 'JEE Advanced', 'NEET': 'NEET' };
    return mapping[test.category] || 'JEE Mains';
  };

  const avgScore = attempts.length > 0 
    ? (attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Navbar */}
      <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
            alt="Pinaka Logo"
            className="h-10 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/test-series')} data-testid="nav-browse-tests-btn">
              <BookOpen className="w-4 h-4 mr-2" />
              Browse Tests
            </Button>
            <ThemeToggle />
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 rounded-3xl p-8 mb-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary">Welcome back!</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-outfit font-bold mb-2">
              Hello, {user?.name || 'Student'}!
            </h1>
            <p className="text-muted-foreground mb-6 max-w-xl">
              Track your progress, continue your preparation, and ace your exams with confidence.
            </p>
            <Button onClick={() => navigate('/test-series')} size="lg" data-testid="explore-btn">
              Explore Test Series
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <BookOpen className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">{purchases.length}</div>
            <div className="text-sm opacity-80">Tests Owned</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <Trophy className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">{attempts.length}</div>
            <div className="text-sm opacity-80">Tests Taken</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <TrendingUp className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">{avgScore}%</div>
            <div className="text-sm opacity-80">Avg Score</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg"
          >
            <Target className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-bold font-mono">
              {attempts.length > 0 ? Math.round((attempts.filter(a => a.score >= 60).length / attempts.length) * 100) : 0}%
            </div>
            <div className="text-sm opacity-80">Pass Rate</div>
          </motion.div>
        </div>

        {/* My Test Series */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-outfit font-bold">My Test Series</h2>
            <Button variant="outline" onClick={() => navigate('/test-series')} data-testid="browse-more-btn">
              Browse More
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {purchases.length === 0 ? (
            <div className="bg-card border-2 border-dashed border-border rounded-2xl p-12 text-center" data-testid="no-purchases">
              <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-outfit font-semibold mb-2">No test series yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Browse our collection of JEE Main, JEE Advanced, and NEET test series to start practicing
              </p>
              <Button onClick={() => navigate('/test-series')} size="lg" data-testid="browse-tests-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Explore Test Series
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {purchases.map((test, index) => {
                const examType = getTestExamType(test);
                const config = EXAM_CONFIG[examType] || EXAM_CONFIG['JEE Mains'];
                const SubjectIcon = SUBJECT_ICONS[test.subject] || BookOpen;
                
                return (
                  <motion.div
                    key={test.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    whileHover={{ y: -4 }}
                    className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer group"
                    onClick={() => navigate(`/test/${test.id}`)}
                    data-testid={`purchased-test-${test.id}`}
                  >
                    {/* Header with gradient */}
                    <div className={`bg-gradient-to-r ${config.color} p-4 text-white`}>
                      <span className="text-xs font-semibold opacity-80 uppercase tracking-wide">{examType}</span>
                      <h3 className="font-bold text-lg line-clamp-1">{test.title}</h3>
                    </div>

                    {/* Body */}
                    <div className="p-5">
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-xs px-2 py-1 rounded-full bg-muted flex items-center gap-1">
                          <SubjectIcon className="w-3 h-3" />
                          {test.subject || 'General'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          test.difficulty === 'Easy' ? 'bg-success/10 text-success' :
                          test.difficulty === 'Hard' ? 'bg-destructive/10 text-destructive' :
                          'bg-warning/10 text-warning'
                        }`}>
                          {test.difficulty}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {test.questions_count} Qs
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {test.duration} min
                        </div>
                      </div>

                      <Button className="w-full group-hover:bg-primary group-hover:text-white transition-colors" variant="outline" data-testid={`start-test-${test.id}`}>
                        Start Test
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Recent Attempts */}
        {attempts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-outfit font-bold mb-6">Recent Attempts</h2>
            <div className="space-y-3">
              {attempts.slice(0, 5).map((attempt, index) => (
                <motion.div
                  key={attempt.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  className="bg-card border border-border rounded-xl p-4 flex items-center justify-between hover:border-primary/30 transition-colors"
                  data-testid={`attempt-row-${attempt.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      attempt.score >= 60 
                        ? 'bg-success/10 text-success' 
                        : 'bg-destructive/10 text-destructive'
                    }`}>
                      {attempt.score >= 60 ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <XCircle className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold">{attempt.test_series_title}</h4>
                      <div className="text-sm text-muted-foreground">
                        {attempt.correct_count}/{attempt.total_questions} correct • {new Date(attempt.submitted_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-2xl font-mono font-bold ${
                      attempt.score >= 60 ? 'text-success' : 'text-destructive'
                    }`}>
                      {attempt.score.toFixed(0)}%
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={(e) => { e.stopPropagation(); navigate(`/results/${attempt.id}`); }}
                      data-testid={`view-result-${attempt.id}`}
                    >
                      View Details
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
